import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import {
  HiArrowLeft,
  HiCheckCircle,
  HiXCircle,
  HiClock,
  HiRefresh,
  HiCog,
  HiEye,
  HiEyeSlash,
  HiShieldCheck,
} from '../components/icons';
import { Button, Card, CardHeader, CardBody, Badge } from '../components/ui';
import { ClioConfigModal } from '../components/tenant/ClioConfigModal';
import { tenantService, type TenantDetail } from '../services/tenant.service';
import { showToast } from '../utils/toast';

export function TenantDetailPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [isClioModalOpen, setIsClioModalOpen] = useState(false);
  const [showSensitiveInfo, setShowSensitiveInfo] = useState(false);
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [isRevokingConnection, setIsRevokingConnection] = useState(false);

  const {
    data: tenant,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => tenantService.getTenantDetail(tenantId!),
    enabled: !!tenantId,
    staleTime: 30000,
  });

  const handleRefreshToken = async () => {
    if (!tenant) return;

    setIsRefreshingToken(true);
    try {
      const result = await tenantService.refreshClioToken(tenant.id);

      if (result.success) {
        showToast.success(result.message);
        if (result.newExpiresAt) {
          showToast.info(`New token expires: ${formatDate(result.newExpiresAt)}`);
        }
      } else {
        showToast.error(result.message);
      }

      refetch();
    } catch (error) {
      showToast.error('Failed to refresh token');
    } finally {
      setIsRefreshingToken(false);
    }
  };

  const handleTestConnection = async () => {
    if (!tenant) return;

    setIsTestingConnection(true);
    try {
      const result = await tenantService.testClioConnection(tenant.id);
      if (result.success) {
        showToast.success(result.message);
        if (result.userInfo) {
          let displayText = `Connected as: ${result.userInfo.name}`;

          if (result.userInfo.email) {
            displayText += ` (${result.userInfo.email})`;
          }

          if (result.userInfo.accountName) {
            displayText += ` - ${result.userInfo.accountName}`;
          }

          showToast.info(displayText);
        }
      } else {
        showToast.error(result.message);
      }
    } catch (error) {
      showToast.error('Failed to test connection');
    } finally {
      setIsTestingConnection(false);
    }
  };

  const handleRevokeConnection = async () => {
    if (!tenant) return;

    const confirmed = window.confirm(
      'Are you sure you want to revoke the Clio connection? This will disconnect the integration and remove all stored tokens.'
    );

    if (!confirmed) return;

    setIsRevokingConnection(true);
    try {
      const result = await tenantService.revokeClioConnection(tenant.id);
      if (result.success) {
        showToast.success(result.message);
        refetch(); // Refresh tenant data to reflect disconnected status
      } else {
        showToast.error(result.message);
      }
    } catch (error) {
      showToast.error('Failed to revoke connection');
    } finally {
      setIsRevokingConnection(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString || dateString.trim() === '') {
      return 'Invalid Date';
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'connected':
        return (
          <Badge variant="success" className="flex items-center gap-1">
            <HiCheckCircle className="h-3 w-3" />
            Connected
          </Badge>
        );
      case 'expired':
        return (
          <Badge variant="warning" className="flex items-center gap-1">
            <HiClock className="h-3 w-3" />
            Token Expired
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="flex items-center gap-1">
            <HiXCircle className="h-3 w-3" />
            Not Connected
          </Badge>
        );
    }
  };

  const getLogLevelBadge = (level: string) => {
    switch (level) {
      case 'error':
        return <Badge variant="error">Error</Badge>;
      case 'warning':
        return <Badge variant="warning">Warning</Badge>;
      default:
        return <Badge variant="secondary">Info</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-xtnd-light dark:bg-xtnd-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-xtnd-dark-200 dark:bg-xtnd-dark-700 rounded w-1/4"></div>
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="h-64 bg-xtnd-dark-200 dark:bg-xtnd-dark-700 rounded-lg"></div>
              <div className="h-64 bg-xtnd-dark-200 dark:bg-xtnd-dark-700 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="min-h-screen bg-xtnd-light dark:bg-xtnd-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <HiXCircle className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-2 text-sm font-medium text-xtnd-dark dark:text-xtnd-white">
              Failed to load tenant details
            </h3>
            <p className="mt-1 text-sm text-xtnd-dark-500 dark:text-xtnd-light">
              {error instanceof Error ? error.message : 'Tenant not found'}
            </p>
            <div className="mt-6 space-x-3">
              <Button onClick={() => refetch()} variant="primary">
                Try Again
              </Button>
              <Link to="/tenants">
                <Button variant="outline">Back to Tenants</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-xtnd-light dark:bg-xtnd-dark-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/tenants"
            className="inline-flex items-center text-xtnd-blue hover:text-xtnd-blue-600 mb-4"
          >
            <HiArrowLeft className="h-4 w-4 mr-1" />
            Back to Tenants
          </Link>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-xtnd-dark dark:text-xtnd-white">
                {tenant.name}
              </h1>
              <p className="mt-2 text-xtnd-dark-600 dark:text-xtnd-light">
                {tenant.domain} • Created {formatDate(tenant.createdAt)}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Badge
                variant={tenant.status === 'active' ? 'success' : 'secondary'}
                className="capitalize"
              >
                {tenant.status}
              </Badge>
              <Button
                variant="primary"
                leftIcon={<HiCog />}
                onClick={() => setIsClioModalOpen(true)}
              >
                Configure Clio
              </Button>
            </div>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Clio Integration Status */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
                  Clio Integration
                </h2>
                {getStatusBadge(tenant.clioConfig.status)}
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              {/* Connection Details */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-light">
                    Client ID
                  </label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="text-sm bg-xtnd-dark-100 dark:bg-xtnd-dark-700 px-2 py-1 rounded font-mono break-all flex-1 dark:text-xtnd-light">
                      {showSensitiveInfo
                        ? tenant.clioConfig.clientId || 'Not configured'
                        : '••••••••••••••••••••••••••••••••••••••••'}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowSensitiveInfo(!showSensitiveInfo)}
                      className="flex-shrink-0"
                    >
                      {showSensitiveInfo ? (
                        <HiEyeSlash className="h-4 w-4" />
                      ) : (
                        <HiEye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="flex items-center">
                  <label className="text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-light">
                    Refresh Token
                  </label>
                  <Badge
                    className="ml-2"
                    variant={tenant.clioConfig.hasRefreshToken ? 'success' : 'secondary'}
                  >
                    {tenant.clioConfig.hasRefreshToken ? 'Available' : 'Not Available'}
                  </Badge>
                </div>
              </div>

              {/* Timestamps */}
              {tenant.clioConfig.lastConnected && (
                <div>
                  <label className="text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-light">
                    Last Connected
                  </label>
                  <p className="text-sm text-xtnd-dark dark:text-xtnd-white mt-1">
                    {formatDate(tenant.clioConfig.lastConnected)}
                  </p>
                </div>
              )}

              {tenant.clioConfig.tokenExpiresAt && (
                <div>
                  <label className="text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-light">
                    Token Expires
                  </label>
                  <p className="text-sm text-xtnd-dark dark:text-xtnd-white mt-1">
                    {formatDate(tenant.clioConfig.tokenExpiresAt)}
                  </p>
                </div>
              )}

              {/* Webhook URLs */}
              {tenant.clioConfig.webhookUrls &&
                Object.keys(tenant.clioConfig.webhookUrls).length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-light">
                      Webhook URLs
                    </label>
                    <div className="mt-2 space-y-2">
                      {Object.entries(tenant.clioConfig.webhookUrls).map(([type, config]) => (
                        <div
                          key={type}
                          className="flex items-center justify-between p-2 bg-xtnd-dark-50 dark:bg-xtnd-dark-800 rounded"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Badge variant={config.enabled ? 'success' : 'secondary'} size="sm">
                                {config.enabled ? 'Enabled' : 'Disabled'}
                              </Badge>
                              <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white capitalize">
                                {type.replace('_', ' ')}
                              </span>
                            </div>
                            <code className="block text-xs bg-xtnd-dark-100 dark:bg-xtnd-dark-700 px-2 py-1 rounded mt-1 break-all">
                              {config.url}
                            </code>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t border-xtnd-light dark:border-xtnd-dark-700">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<HiShieldCheck />}
                  onClick={handleTestConnection}
                  disabled={tenant.clioConfig.status !== 'connected'}
                  loading={isTestingConnection}
                >
                  Test Connection
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<HiRefresh />}
                  onClick={handleRefreshToken}
                  disabled={!tenant.clioConfig.hasRefreshToken}
                  loading={isRefreshingToken}
                >
                  Refresh Token
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  leftIcon={<HiXCircle />}
                  onClick={handleRevokeConnection}
                  disabled={tenant.clioConfig.status !== 'connected'}
                  loading={isRevokingConnection}
                >
                  Revoke
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Configuration */}
          <Card>
            <CardHeader>
              <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
                Configuration
              </h2>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="grid gap-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white">
                    Email Notifications
                  </span>
                  <Badge
                    variant={tenant.configuration.emailNotifications ? 'success' : 'secondary'}
                  >
                    {tenant.configuration.emailNotifications ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white">
                    PDF Generation
                  </span>
                  <Badge variant={tenant.configuration.pdfGeneration ? 'success' : 'secondary'}>
                    {tenant.configuration.pdfGeneration ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white">
                    Webhook Processing
                  </span>
                  <Badge variant={tenant.configuration.webhookEnabled ? 'success' : 'secondary'}>
                    {tenant.configuration.webhookEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Recent Logs */}
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
                Recent Activity Logs
              </h2>
              <Button
                variant="outline"
                size="sm"
                leftIcon={<HiRefresh />}
                onClick={() => refetch()}
              >
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {tenant.logs.length > 0 ? (
              <div className="space-y-3">
                {tenant.logs.slice(0, 10).map((log: TenantDetail['logs'][0]) => (
                  <div
                    key={log.id}
                    className="flex items-start gap-3 p-3 bg-xtnd-dark-50 dark:bg-xtnd-dark-800 rounded-lg"
                  >
                    <div className="flex-shrink-0">{getLogLevelBadge(log.level)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-xtnd-dark dark:text-xtnd-white">{log.message}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-xtnd-dark-500 dark:text-xtnd-light">
                        <span>{formatDate(log.timestamp)}</span>
                        <span>•</span>
                        <span>{log.source}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-xtnd-dark-500 dark:text-xtnd-light">
                  No recent activity logs found
                </p>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Clio Config Modal */}
      <ClioConfigModal
        open={isClioModalOpen}
        onClose={() => {
          setIsClioModalOpen(false);
          refetch();
        }}
        tenantId={tenant.id}
        tenantName={tenant.name}
      />
    </div>
  );
}
